import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Smartphone, Globe, Zap, Bell, Download, Check, 
  Wifi, WifiOff, ArrowRight, Sparkles 
} from 'lucide-react';

export default function HomePage() {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsInstallable(false);
    }
    
    setDeferredPrompt(null);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full font-semibold text-sm mb-6">
          <Sparkles className="w-4 h-4" />
          Progressive Web App Demo
        </div>
        <h1 className="text-5xl font-black text-gray-900 mb-4">
          PWAs: The Future of Apps
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Build once, deploy everywhere. No app store required. Instant updates. 
          Native-like experience on any device.
        </p>
      </div>

      {/* Installation Status */}
      {isInstalled && (
        <div className="max-w-2xl mx-auto mb-8 bg-green-50 border-2 border-green-200 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-green-900 text-lg">PWA Installed! 🎉</h3>
              <p className="text-green-700">This app is now on your home screen</p>
            </div>
          </div>
        </div>
      )}

      {isInstallable && !isInstalled && (
        <div className="max-w-2xl mx-auto mb-8 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-xl mb-2">Install This App</h3>
              <p className="text-white/90">Add to your home screen for a native app experience</p>
            </div>
            <button
              onClick={handleInstall}
              className="bg-white text-orange-600 px-8 py-3 rounded-xl font-bold hover:bg-orange-50 transition-all flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Install Now
            </button>
          </div>
        </div>
      )}

      {/* What is a PWA */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
        <h2 className="text-3xl font-black text-gray-900 mb-6">What is a Progressive Web App?</h2>
        <p className="text-lg text-gray-700 mb-6">
          A PWA is a website that <span className="font-bold text-orange-600">acts like a native app</span>. 
          Users can install it on their device, receive push notifications, and use it offline - 
          all without downloading from an app store.
        </p>

        <div className="grid md:grid-2 gap-6">
          <div className="flex gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">It's a Website</h3>
              <p className="text-gray-600">Accessible via URL, works on any device with a browser</p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Feels Like an App</h3>
              <p className="text-gray-600">Installs to home screen, full-screen mode, app-like navigation</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
        <h2 className="text-3xl font-black text-gray-900 mb-8">Why Choose PWA Over Native Apps?</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-2">Instant Updates</h3>
            <p className="text-gray-700 mb-3">
              No app store review process. Push updates instantly to all users. 
              Users always have the latest version.
            </p>
            <div className="text-sm text-green-700 font-semibold">
              ⏱️ Deploy: Minutes vs Days/Weeks
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
              <Download className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-2">No App Store Hassle</h3>
            <p className="text-gray-700 mb-3">
              Skip App Store/Play Store approval. No 30% commission fees. 
              Direct distribution to users.
            </p>
            <div className="text-sm text-blue-700 font-semibold">
              💰 Save: 30% commission + review time
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200">
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-2">Push Notifications</h3>
            <p className="text-gray-700 mb-3">
              Send notifications just like native apps. Engage users even when they're not using your app.
            </p>
            <Link 
              to="/notifications" 
              className="text-purple-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              Try Demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border-2 border-orange-200">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
              <WifiOff className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-2">Works Offline</h3>
            <p className="text-gray-700 mb-3">
              Service workers cache content. Users can access your app even without internet connection.
            </p>
            <div className="text-sm text-orange-700 font-semibold">
              📱 Better UX in low/no connectivity
            </div>
          </div>
        </div>
      </div>

      {/* Cost Comparison */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl shadow-xl p-8 mb-8">
        <h2 className="text-3xl font-black mb-8">Cost & Time Comparison</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xl font-bold mb-4 text-orange-400">Native App Development</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-red-400">❌</span>
                <span>Build separate iOS & Android apps</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400">❌</span>
                <span>2-4 weeks for app store approval</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400">❌</span>
                <span>30% commission on in-app purchases</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400">❌</span>
                <span>Users must manually update apps</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400">❌</span>
                <span>Large download sizes (50MB+)</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-green-400">PWA Development</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-400">✅</span>
                <span>Build once, works everywhere</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✅</span>
                <span>Deploy instantly, no approval needed</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✅</span>
                <span>Zero commission fees</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✅</span>
                <span>Automatic updates for all users</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✅</span>
                <span>Lightweight, fast loading</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 p-6 bg-orange-500/20 border-2 border-orange-500 rounded-xl">
          <p className="text-xl font-bold text-center">
            💡 <span className="text-orange-400">Faster development.</span> Lower costs. Better iteration speed.
          </p>
        </div>
      </div>

      {/* Call to Action */}
      <div className="grid md:grid-cols-2 gap-6">
        <Link 
          to="/guide"
          className="bg-white hover:shadow-2xl transition-all rounded-2xl p-8 border-2 border-gray-200 hover:border-orange-300 group"
        >
          <Smartphone className="w-12 h-12 text-orange-500 mb-4 group-hover:scale-110 transition-transform" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Installation Guide</h3>
          <p className="text-gray-600 mb-4">
            Step-by-step instructions for Android & iOS
          </p>
          <span className="text-orange-600 font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all">
            Learn How <ArrowRight className="w-5 h-5" />
          </span>
        </Link>

        <Link 
          to="/notifications"
          className="bg-gradient-to-br from-orange-500 to-amber-500 hover:shadow-2xl transition-all rounded-2xl p-8 text-white group"
        >
          <Bell className="w-12 h-12 mb-4 group-hover:scale-110 transition-transform" />
          <h3 className="text-2xl font-bold mb-2">Try Notifications</h3>
          <p className="text-white/90 mb-4">
            See push notifications in action
          </p>
          <span className="font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all">
            Live Demo <ArrowRight className="w-5 h-5" />
          </span>
        </Link>
      </div>
    </div>
  );
}
